import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Flame, Thermometer, Snowflake, Wind } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPrice } from "@/utils/currency";
import { useAuth } from "@/contexts/AuthContext";

const TEMP_CONFIG = {
  hot:     { label: "Ativo",    icon: Flame,       className: "bg-red-100 text-red-700" },
  warm:    { label: "Morno",    icon: Thermometer,  className: "bg-amber-100 text-amber-700" },
  cooling: { label: "A Arrefecer", icon: Wind,     className: "bg-blue-100 text-blue-700" },
  cold:    { label: "Inativo",  icon: Snowflake,    className: "bg-gray-100 text-gray-600" },
};

const VIP_CONFIG = {
  Black:  { className: "bg-gray-900 text-white" },
  Gold:   { className: "bg-yellow-100 text-yellow-800" },
  Silver: { className: "bg-gray-100 text-gray-700" },
  Bronze: { className: "bg-amber-50 text-amber-700" },
};

function TempBadge({ temp }) {
  const cfg = TEMP_CONFIG[temp] || TEMP_CONFIG.cold;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.className} gap-1 text-xs`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </Badge>
  );
}

function RiskBar({ score }) {
  const color = score >= 70 ? "bg-red-500" : score >= 40 ? "bg-amber-400" : "bg-emerald-400";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-gray-500">{score}</span>
    </div>
  );
}

export function CustomerIntelligenceTab() {
  const { authAxios } = useAuth();
  const axiosRef = useRef(authAxios);
  axiosRef.current = authAxios;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [temperature, setTemperature] = useState("");
  const [vipLevel, setVipLevel] = useState("");
  const [sortBy, setSortBy] = useState("risk_score");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, limit: 100 });
      if (temperature) params.set("temperature", temperature);
      if (vipLevel) params.set("vip_level", vipLevel);
      const res = await axiosRef.current.get(`/intelligence/customers/insights?${params}`);
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar inteligência de clientes");
    }
    setLoading(false);
  }, [temperature, vipLevel, sortBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  const customers = data?.customers || [];
  const hotCount = customers.filter(c => c.customer_temperature === "hot").length;
  const coldCount = customers.filter(c => c.customer_temperature === "cold").length;
  const highRisk = customers.filter(c => c.risk_score >= 70).length;

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{hotCount}</p>
          <p className="text-xs text-red-400">Clientes ativos</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{highRisk}</p>
          <p className="text-xs text-amber-400">Alto risco churn</p>
        </div>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-gray-600">{coldCount}</p>
          <p className="text-xs text-gray-400">Inativos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={temperature} onValueChange={setTemperature}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Temperatura" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">Todas</SelectItem>
            <SelectItem value="hot">Ativo</SelectItem>
            <SelectItem value="warm">Morno</SelectItem>
            <SelectItem value="cooling">A Arrefecer</SelectItem>
            <SelectItem value="cold">Inativo</SelectItem>
          </SelectContent>
        </Select>
        <Select value={vipLevel} onValueChange={setVipLevel}>
          <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">Todos</SelectItem>
            <SelectItem value="Black">Black</SelectItem>
            <SelectItem value="Gold">Gold</SelectItem>
            <SelectItem value="Silver">Silver</SelectItem>
            <SelectItem value="Bronze">Bronze</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Ordenar" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="risk_score">Risco de Churn</SelectItem>
            <SelectItem value="predicted_ltv">LTV Previsto</SelectItem>
            <SelectItem value="total_spent">Total Gasto</SelectItem>
            <SelectItem value="days_since_last_order">Dias sem pedido</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={fetchData} className="h-8 text-xs">Atualizar</Button>
      </div>

      {/* Table */}
      {customers.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          Nenhum dado de inteligência disponível ainda.<br />
          <span className="text-xs">O motor corre diariamente às 2:30.</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Pedidos</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">LTV Previsto</th>
                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Risco</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {customers.map(c => (
                <tr key={c.phone} className="hover:bg-gray-50 transition-colors">
                  <td className="p-3">
                    <p className="font-medium text-gray-800">{c.name || "—"}</p>
                    <p className="text-xs text-gray-400">{c.phone}</p>
                    {c.favorite_products?.length > 0 && (
                      <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[160px]">
                        ❤ {c.favorite_products.slice(0, 2).join(", ")}
                      </p>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      <TempBadge temp={c.customer_temperature} />
                      <Badge className={`text-[10px] px-1.5 ${(VIP_CONFIG[c.vip_level] || VIP_CONFIG.Bronze).className}`}>
                        {c.vip_level || "Bronze"}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <p className="font-medium">{c.total_orders}</p>
                    <p className="text-xs text-gray-400">{c.days_since_last_order}d atrás</p>
                  </td>
                  <td className="p-3 text-right">
                    <p className="font-medium text-emerald-600">{formatPrice(c.total_spent)}</p>
                    <p className="text-xs text-gray-400">Ticket: {formatPrice(c.avg_ticket)}</p>
                  </td>
                  <td className="p-3 text-right">
                    <p className="font-semibold text-purple-600">{formatPrice(c.predicted_ltv)}</p>
                    <p className="text-xs text-gray-400">
                      {c.avg_order_interval > 0 ? `~${Math.round(c.avg_order_interval)}d/pedido` : ""}
                    </p>
                  </td>
                  <td className="p-3">
                    <RiskBar score={c.risk_score || 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
